import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LoginModal from '@/components/LoginModal';
import { Helmet } from 'react-helmet-async';
import { handleNavigationScroll, handleTabScroll } from '@/utils/scrollUtils';

// Constants
const FOOTER_CONTENT = {
  company: {
    description: "AI-powered product operations platform that transforms customer feedback into actionable insights for revenue and product teams.",
    social: {
      linkedin: "https://www.linkedin.com/company/strive-ai/",
      youtube: "https://www.youtube.com/@strive-ai/videos"
    }
  },
  sections: [
    {
      title: "Knowledge Intelligence",
      items: [
        { id: "document-uploads", label: "Upload any document" },
        { id: "web-insights", label: "Monitor Specific Websites" },
        { id: "web-crawler", label: "Automatic Web Search" },
        { id: "knowledge-notes", label: "Train your AI" },
        { id: "system-integrations", label: "Integrations" }
      ]
    },
    {
      title: "Artificial Intelligence",
      items: [
        { id: "how-it-works", label: "How It Works" },
        { id: "workflow-jobs", label: "Workflow Templates", action: "templates" },
        { id: "workflow-jobs", label: "Custom Workflows", action: "custom" },
        { id: "chat-with-data", label: "Chat with Data" }
      ]
    },
    {
      title: "Solutions",
      items: [
        { id: "product-management", label: "Product Teams" },
        { id: "revenue-intelligence", label: "Revenue Teams" },
        { to: "/pricing", label: "Pricing" },
        { to: "/faq", label: "FAQ" }
      ]
    },
    {
      title: "Company",
      items: [
        { to: "/about", label: "About" },
        { href: "https://matters-of-context.strive.us/", label: "Blog", external: true },
        { to: "/contact", label: "Contact", state: { tab: 'schedule' } },
        { to: "/glossary", label: "Glossary" },
        { to: "/contact", label: "Newsletter Signup", state: { tab: 'newsletter' } }
      ]
    }
  ]
} as const;

const Footer = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleLoginClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://demo-redirect.striveus.workers.dev/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (data.success === true && data.redirectUrl) {
        window.open(data.redirectUrl, '_blank');
      } else {
        setIsLoginModalOpen(true);
      }
    } catch (err) {
      console.error('Error checking auth:', err);
      setIsLoginModalOpen(true);
    }
  };

  const handleSectionClick = useCallback((e: React.MouseEvent, path: string, sectionId: string, action?: string) => {
    e.preventDefault();
    
    // If this is the workflow section and we have an action
    if (sectionId === 'workflow-jobs' && action) {
      // First navigate to home page if not already there
      if (window.location.pathname !== '/') {
        navigate('/', { 
          state: { 
            scrollTo: sectionId,
            tabAction: action
          }
        });
        return;
      }
      
      // If already on home page, just trigger tab change
      const tabId = action === 'templates' ? 'templates-tab' : 'custom-tab';
      handleTabScroll(tabId, sectionId);
      return;
    }
    
    // Special handling for Knowledge Intelligence page
    if (path === '/knowledge-intelligence') {
      // For Knowledge Intelligence, just navigate with the hash and let the page handle scrolling
      navigate(`${path}#${sectionId}`);
      return;
    }

    // Otherwise handle navigation with scroll
    handleNavigationScroll(path, sectionId);
  }, [navigate]);

  // Enhanced structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Footer",
    "name": "Strive Footer",
    "description": FOOTER_CONTENT.company.description,
    "url": window.location.origin,
    "mainEntity": {
      "@type": "Organization",
      "name": "Strive",
      "url": window.location.origin,
      "logo": `${window.location.origin}/White_Logomark.svg`,
      "sameAs": [
        FOOTER_CONTENT.company.social.linkedin,
        FOOTER_CONTENT.company.social.youtube
      ]
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <footer 
        className="bg-gray-900 text-gray-100 px-4 sm:px-6 md:px-10 py-12 sm:py-16"
        role="contentinfo"
        aria-label="Site footer"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-6 sm:gap-x-8 gap-y-8 sm:gap-y-12 mb-6">
            {/* Company Info */}
            <div className="lg:col-span-4">
              <div className="mb-4 sm:mb-6">
                <Link to="/" aria-label="Return to homepage" onClick={(e) => {
                  e.preventDefault();
                  handleNavigationScroll('/', null);
                }}>
                  <img 
                    src="/White_Logomark.svg" 
                    alt="Strive Logo" 
                    className="h-8 w-auto"
                    loading="lazy"
                    width="32"
                    height="32"
                  />
                </Link>
              </div>
              <p className="text-gray-100 mb-6 text-sm leading-relaxed">
                {FOOTER_CONTENT.company.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <a 
                  href="https://trust.strive.us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-90 transition-opacity"
                  aria-label="Visit our Trust Center"
                >
                  <img 
                    src="/logos/SOC 2 badge.png" 
                    alt="SOC 2 Type I Certified" 
                    className="h-16 w-auto"
                    loading="lazy"
                  />
                </a>
                <div className="text-sm text-gray-300 text-center sm:text-left">
                  <p className="font-medium">SOC 2 Type I Certified</p>
                  <p className="text-xs">Type II by July 2025</p>
                </div>
              </div>
              <div className="flex justify-center sm:justify-start" aria-label="Social media links">
                <ul className="flex space-x-4" role="list" aria-label="Social media links">
                  <li role="listitem">
                    <a 
                      href={FOOTER_CONTENT.company.social.linkedin}
                      className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit our LinkedIn page"
                    >
                      <span className="sr-only">Visit our LinkedIn page</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                  </li>
                  <li role="listitem">
                    <a 
                      href={FOOTER_CONTENT.company.social.youtube}
                      className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit our YouTube channel"
                    >
                      <span className="sr-only">Visit our YouTube channel</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Navigation Links */}
            <nav 
              className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
              aria-label="Footer navigation"
            >
              {FOOTER_CONTENT.sections.map((section) => (
                <div key={section.title} className="space-y-6">
                  <div>
                    <h2 className="text-white font-semibold mb-3 text-sm">
                      {section.title}
                    </h2>
                    <ul 
                      className="space-y-2"
                      role="list"
                      aria-label={`${section.title} links`}
                    >
                      {section.items.map((item) => (
                        <li key={item.label} role="listitem">
                          {item.external ? (
                            <a
                              href={item.href}
                              className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`${item.label} (opens in new tab)`}
                            >
                              {item.label}
                            </a>
                          ) : item.to && item.label === "FAQ" ? (
                            <Link
                              to={item.to}
                              state={item.state}
                              className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigationScroll(item.to, null);
                              }}
                            >
                              {item.label}
                            </Link>
                          ) : item.to ? (
                            <Link
                              to={item.to}
                              state={item.state}
                              className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                              onClick={(e) => {
                                e.preventDefault();
                                // If we have state (like for newsletter tab), use navigate directly
                                if (item.state) {
                                  navigate(item.to, { state: item.state });
                                } else {
                                  handleNavigationScroll(item.to, null);
                                }
                              }}
                            >
                              {item.label}
                            </Link>
                          ) : (
                            <Link
                              to={section.title === "Knowledge Intelligence" ? "/knowledge-intelligence" : "/"}
                              className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded-full"
                              onClick={(e) => handleSectionClick(
                                e,
                                section.title === "Knowledge Intelligence" ? "/knowledge-intelligence" : "/",
                                item.id,
                                item.action
                              )}
                            >
                              {item.label}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Footer Bottom */}
          <div 
            className="pt-3 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center sm:items-end space-y-4 sm:space-y-0 mb-20"
            role="contentinfo"
            aria-label="Legal information"
          >
            <div className="text-center sm:text-left">
              <p className="text-gray-100 text-xs mb-1">
                &copy; {new Date().getFullYear()} Strive Software, Inc. All rights reserved.
              </p>
              <address className="text-gray-400 text-xs not-italic">
                600 1st Ave Ste 330 PMB 94465, Seattle, WA 98104
              </address>
            </div>
            <div>
              <ul className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6" role="list" aria-label="Legal links">
                <li role="listitem">
                  <Link
                    to="/terms"
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    aria-label="Read our Terms of Service"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationScroll('/terms', null);
                    }}
                  >
                    Terms
                  </Link>
                </li>
                <li role="listitem">
                  <Link
                    to="/dpa"
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    aria-label="Read our Data Processing Agreement"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationScroll('/dpa', null);
                    }}
                  >
                    DPA
                  </Link>
                </li>
                <li role="listitem">
                  <Link
                    to="/privacy"
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    aria-label="Read our Privacy Policy"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationScroll('/privacy', null);
                    }}
                  >
                    Privacy
                  </Link>
                </li>
                <li role="listitem">
                  <Link
                    to="/cookies"
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    aria-label="Read our Cookie Policy"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationScroll('/cookies', null);
                    }}
                  >
                    Cookies
                  </Link>
                </li>
                <li role="listitem">
                  <a
                    href="https://trust.strive.us"
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Trust Center (opens in new tab)"
                  >
                    Trust Center
                  </a>
                </li>
                <li role="listitem">
                  <button
                    onClick={handleLoginClick}
                    className="text-gray-100 hover:text-white transition-colors focus:outline-none rounded"
                    aria-label="Login to your account"
                  >
                    Login
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <LoginModal 
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
        />
      </footer>
    </>
  );
};

export default Footer;
